import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Box, Card, Container, Typography, Stack, Divider, Button, Fade, Grow} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Page from 'src/components/Page';
import {Info} from '@mui/icons-material';

export default function Thanks() {
	const location = useLocation();
	const navigate = useNavigate();
	const {data} = location.state ?? {};
	const {due_date, amount, expiration_date} = data || {};

	// Animation state
	const [showContent, setShowContent] = React.useState(false);

	// Trigger animations on component mount
	React.useEffect(() => {
		const timer = setTimeout(() => {
			setShowContent(true);
		}, 100);
		return () => clearTimeout(timer);
	}, []);

	// Format dates in Arabic
	const formatArabicDate = (dateString) => {
		if (!dateString) return 'غير محدد';
		try {
			return new Date(dateString).toLocaleDateString('ar-AE', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
				weekday: 'long',
			});
		} catch (error) {
			return 'تاريخ غير صحيح';
		}
	};

	const handleGoToDashboard = () => {
		window.location.href = '/';
	};

	if (!data) return;

	return (
		<Page title="تأكيد تجديد الاشتراك">
			<Fade in={showContent} timeout={800}>
				<Container maxWidth="md" sx={{py: 4}}>
					<Grow in={showContent} timeout={1000}>
						<Box sx={{textAlign: 'center', mb: 4}}>
							{/* Success Icon */}
							<CheckCircleIcon
								sx={{
									mt: 4,
									fontSize: 80,
									mb: 2,
									color: '#076458',
									filter: 'drop-shadow(0px 8px 16px rgba(7, 100, 88, 0.15))',
								}}
							/>

							<Typography
								variant="h3"
								sx={{
									fontWeight: 'bold',
									color: 'text.primary',
									mb: 2,
									fontSize: {xs: '1.8rem', md: '2.5rem'},
								}}
							>
								تم تجديد الإشتراك بنجاح!
							</Typography>
						</Box>
					</Grow>

					<Fade in={showContent} timeout={1200} style={{transitionDelay: showContent ? '300ms' : '0ms'}}>
						<Stack
							flex={1}
							justifyContent="center"
							direction={{xs: 'column', sm: 'row'}}
							maxWidth="sm"
							margin="0 auto"
							gap={2}
						>
							{[
								{
									label: 'تاريخ انتهاء الاشتراك',
									value: formatArabicDate(expiration_date),
								},
								{
									label: 'قيمة الإشتراك',
									value: `${amount} دينار`,
								},
								{
									label: 'تاريخ استحقاق الدفع',
									value: formatArabicDate(due_date),
								},
							].map((item, idx) => (
								<Card
									elevation={0}
									variant="outlined"
									sx={{
										boxShadow: 0,
										p: 2,
										flex: idx === 1 ? 0.5 : 1,
										minWidth: 0,
										width: {xs: '100%', sm: 'auto'},
									}}
									key={idx}
								>
									<Typography variant="caption" sx={{opacity: 0.8, fontSize: '0.75rem'}}>
										{item.label}
									</Typography>
									<Typography
										variant="subtitle1"
										sx={{fontWeight: 'bold', color: '#4E9E91', fontSize: '1rem'}}
									>
										{item.value}
									</Typography>
								</Card>
							))}
						</Stack>
					</Fade>

					<Fade in={showContent} timeout={1000} style={{transitionDelay: showContent ? '600ms' : '0ms'}}>
						<Card
							sx={{
								mt: 4,
								maxWidth: 'sm',
								mx: 'auto',
								p: 3,
								mb: 4,
								borderRadius: 2,
								border: '2px solid',
								borderColor: '#F3BB37',
								backgroundColor: 'rgba(243, 187, 55, 0.1)',
							}}
						>
							<Stack direction="row" alignItems="center" spacing={2}>
								<Info
									sx={{
										fontSize: 24,
										color: '#F3BB37',
										mt: 0.5,
									}}
								/>
								<Typography variant="body1" sx={{color: '#076458', lineHeight: 1.6}}>
									سيتم التواصل معك من قبل مندوبنا لإستلام قيمة الأشتراك
								</Typography>
								<Box>
									{/* <Typography variant="h6" sx={{fontWeight: 'bold', color: '#076458', mb: 1}}>
										ملاحظة
									</Typography> */}
								</Box>
							</Stack>
						</Card>
					</Fade>

					<Fade in={showContent} timeout={800} style={{transitionDelay: showContent ? '900ms' : '0ms'}}>
						<Box>
							{/* Action Buttons */}
							<Box sx={{textAlign: 'center'}}>
								<Button
									variant="contained"
									size="large"
									onClick={handleGoToDashboard}
									sx={{
										px: 4,
										py: 1.5,
										borderRadius: 2,
										fontSize: '1.1rem',
										fontWeight: 'bold',
										background: 'linear-gradient(45deg, #076458 30%, #4DBDAA 90%)',
										boxShadow: '0 3px 5px 2px rgba(7, 100, 88, .3)',
										'&:hover': {
											background: 'linear-gradient(45deg, #02393E 30%, #076458 90%)',
											boxShadow: '0 6px 10px 4px rgba(7, 100, 88, .3)',
										},
									}}
								>
									الإنتقال للصفحة الرئيسية
								</Button>
							</Box>
							{/* Footer Message */}
							<Box sx={{textAlign: 'center', mt: 4, pt: 3}}>
								<Divider sx={{mb: 3}} />
								<Typography variant="body2" sx={{color: 'text.secondary'}}>
									شكراً لاختيارك خدماتنا..
								</Typography>
							</Box>
						</Box>
					</Fade>
				</Container>
			</Fade>
		</Page>
	);
}
